import React from 'react';
import { Result, Button, Typography } from '@arco-design/web-react';
import { IconExclamationCircleFill, IconRefresh } from '@arco-design/web-react/icon';
import ErrorService from './ErrorService';
import i18n from '@/i18n';

const tc = (key, options) => i18n.t(key, { ns: 'common', ...options });

const { Text } = Typography;

/**
 * ErrorBoundary Único y Universal
 * Un solo componente para manejar TODOS los errores de la plataforma
 *
 * Props:
 * - level: 'app' | 'page' | 'component' (default: 'component')
 * - onReset: Callback al resetear
 * - onCancel: Callback al cancelar
 * - resetKeys: Array de valores que al cambiar resetean el boundary
 * - title: Título personalizado del error
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const { level = 'component' } = this.props;

    // Usar ErrorService para logging
    ErrorService.logError(error, `ErrorBoundary-${level}`, {
      componentStack: errorInfo.componentStack,
      props: this.props
    });

    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Si es stale chunk tras deploy, recargar con cache-bust
    if (ErrorService.isChunkLoadError(error)) {
      setTimeout(() => ErrorService.reloadAfterChunkError(), 2000);
      return;
    }

    // Si es otro error crítico, recargar simple
    if (ErrorService.isCriticalError(error)) {
      setTimeout(() => window.location.reload(), 3000);
    }

    // Detectar loops
    if (this.state.errorCount > 5) {
      console.error('⚠️ Loop de errores detectado');
      setTimeout(() => window.location.reload(), 2000);
    }
  }

  componentDidUpdate(prevProps) {
    // Reset automático si cambian las resetKeys
    if (this.props.resetKeys && prevProps.resetKeys) {
      const hasChanged = this.props.resetKeys.some(
        (key, index) => key !== prevProps.resetKeys[index]
      );
      if (hasChanged) this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    });
  };

  handleReset = () => {
    this.resetErrorBoundary();
    this.props.onReset?.();
  };

  handleCancel = () => {
    this.resetErrorBoundary();
    this.props.onCancel?.();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { level = 'component', title } = this.props;
    const isDev = import.meta.env.DEV;

    // Configuración según nivel
    const config = {
      app: {
        title: title || tc('errorBoundary.critical'),
        subtitle: tc('errorBoundary.criticalDesc'),
        style: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }
      },
      page: {
        title: title || tc('errorBoundary.pageError'),
        subtitle: tc('errorBoundary.pageErrorDesc'),
        style: { minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }
      },
      component: {
        title: title || tc('errorBoundary.componentError'),
        subtitle: ErrorService.getUserMessage(this.state.error),
        style: { padding: '40px' }
      }
    }[level];

    return (
      <div style={config.style}>
        <Result
          status="error"
          icon={<IconExclamationCircleFill />}
          title={config.title}
          subTitle={config.subtitle}
          extra={[
            this.props.onCancel && (
              <Button key="cancel" onClick={this.handleCancel}>
                {tc('actions.back')}
              </Button>
            ),
            <Button key="retry" type="primary" icon={<IconRefresh />} onClick={this.handleReset}>
              {tc('actions.retry')}
            </Button>,
            <Button key="reload" onClick={() => window.location.reload()}>
              {tc('actions.reload')}
            </Button>
          ].filter(Boolean)}
        >
          {isDev && this.state.error && (
            <details style={{ marginTop: 20, textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', color: '#666' }}>
                {tc('errorBoundary.technicalDetails')}
              </summary>
              <pre style={{
                marginTop: 10,
                padding: 15,
                backgroundColor: '#f5f5f5',
                borderRadius: 4,
                fontSize: 11,
                overflow: 'auto'
              }}>
                {this.state.error.stack}
                {'\n\nComponent Stack:'}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </Result>
      </div>
    );
  }
}

export default ErrorBoundary;